import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { type CreateUserInput, createUserInputSchema, MIN_PASSWORD_LENGTH } from 'used-vehicle-contracts';
import { orpc } from '../../../apis/orpc-query';
import { Button } from '../../../components/ui/Button';
import { FormField } from '../../../components/ui/FormField';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Sheet } from '../../../components/ui/Sheet';
import { serverMessage, submitErrorKey } from '../../../lib/form-helpers';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';

export interface UserSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Create a user and seat it in the current tenant. Editing a role or a status is done from the row. */
export const UserSheet = ({ isOpen, onClose }: UserSheetProps) => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateUserInput>({
    resolver: zodResolver(createUserInputSchema),
    defaultValues: { email: '', name: '', role: 'SALES', password: '' },
  });

  const createUser = useMutation(
    orpc.users.create.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.users.list.key() });
        reset();
        onClose();
      },
      onError: (error: unknown) => setSubmitError(serverMessage(error) ?? t(submitErrorKey(error))),
    }),
  );

  const close = (): void => {
    setSubmitError(null);
    reset();
    onClose();
  };

  const onSubmit = handleSubmit((values) => {
    setSubmitError(null);
    createUser.mutate(values);
  });

  return (
    <Sheet
      isOpen={isOpen}
      onClose={close}
      title={t('users.createTitle')}
      description={t('users.createSubtitle')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={close}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            type="submit"
            form="create-user-form"
            isLoading={createUser.isPending}
          >
            {createUser.isPending ? t('actions.saving') : t('actions.create')}
          </Button>
        </>
      }
    >
      <form
        id="create-user-form"
        onSubmit={onSubmit}
        noValidate
        className="space-y-4"
      >
        <FormField
          id="user-name"
          label={t('users.name')}
          error={errors.name?.message}
        >
          <Input
            id="user-name"
            autoFocus
            hasError={Boolean(errors.name)}
            {...register('name')}
          />
        </FormField>

        <FormField
          id="user-email"
          label={t('users.email')}
          error={errors.email?.message}
        >
          <Input
            id="user-email"
            type="email"
            dir="ltr"
            className="text-start"
            placeholder={t('users.emailPlaceholder')}
            hasError={Boolean(errors.email)}
            {...register('email')}
          />
        </FormField>

        <FormField
          id="user-role"
          label={t('users.role')}
          hint={t('users.roleHint')}
          error={errors.role?.message}
        >
          <Select
            id="user-role"
            options={[
              { value: 'SALES', label: t('roles.SALES') },
              { value: 'ADMIN', label: t('roles.ADMIN') },
            ]}
            hasError={Boolean(errors.role)}
            {...register('role')}
          />
        </FormField>

        <FormField
          id="user-password"
          label={t('users.initialPassword')}
          hint={t('users.initialPasswordHint', { min: MIN_PASSWORD_LENGTH })}
          error={errors.password?.message}
        >
          <Input
            id="user-password"
            type="password"
            autoComplete="new-password"
            dir="ltr"
            className="text-start"
            hasError={Boolean(errors.password)}
            {...register('password')}
          />
        </FormField>

        {submitError ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {submitError}
          </p>
        ) : null}
      </form>
    </Sheet>
  );
};
